<template>
  <div class="fixed top-4 inset-x-0 z-50 flex flex-col items-center gap-3 px-4 pointer-events-none">
    <div v-if="showConfetti" class="absolute top-10 left-1/2 -translate-x-1/2">
      <ConfettiExplosion :particleCount="220" :force="0.6" :stageHeight="900" :duration="3500"
                         :colors="['#2dd4bf', '#4ade80', '#facc15', '#f472b6', '#ffffff']" />
    </div>
    <TransitionGroup name="toast">
      <div v-for="a in toastQueue" :key="a.id"
           class="achievement-card pointer-events-auto relative flex items-center gap-4 w-96 max-w-full rounded-2xl px-5 py-4 text-left overflow-hidden">
        <span class="achievement-icon shrink-0 w-16 h-16 flex items-center justify-center text-5xl leading-none">{{ a.icon }}</span>
        <div class="min-w-0 flex-1">
          <div class="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-300 leading-tight flex items-center gap-1.5">
            <span class="sparkle">✦</span> Achievement unlocked
          </div>
          <div class="font-extrabold text-lg text-white leading-tight mt-0.5">{{ a.name }}</div>
          <div class="text-sm text-gray-300 leading-snug mt-0.5">{{ a.description }}</div>
        </div>
        <button type="button" aria-label="Close"
                class="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors leading-none"
                @click="close(a.id)">
          <span class="text-lg leading-none">&times;</span>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import {ref, watch} from "vue";
import ConfettiExplosion from "vue-confetti-explosion";
import {useAchievements} from "../composables/useAchievements.js";
import {useSound} from "../composables/useSound.js";

const {toastQueue, dismissToast} = useAchievements();
const {playAchievement} = useSound();

const showConfetti = ref(false);

// Auto-dismiss each toast a while after it appears. `timers` holds the live timeout per id so
// closing a toast early can cancel its pending dismiss. The unlock sound and confetti fire once
// per batch, not per toast, so a backfill burst doesn't stack a dozen overlapping chimes.
const timers = new Map();

function close(id) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  dismissToast(id);
}

watch(toastQueue, (queue) => {
  let isNewBatch = false;
  for (const a of queue) {
    if (!timers.has(a.id)) {
      isNewBatch = true;
      timers.set(a.id, setTimeout(() => close(a.id), 10000));
    }
  }
  if (isNewBatch) {
    playAchievement();
    // Remount the confetti so it replays for each new batch.
    showConfetti.value = false;
    requestAnimationFrame(() => { showConfetti.value = true; });
  }
}, {deep: true, immediate: true});
</script>

<style scoped>
.achievement-card {
  background: linear-gradient(135deg, #0f2a2a 0%, #111827 60%);
  border: 1px solid rgba(45, 212, 191, 0.6);
  box-shadow: 0 0 0 1px rgba(45, 212, 191, 0.15),
              0 8px 30px rgba(0, 0, 0, 0.5),
              0 0 24px rgba(45, 212, 191, 0.25);
}

/* Sweeping shine across the card on entry. */
.achievement-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: -75%;
  width: 50%;
  height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.18), transparent);
  transform: skewX(-20deg);
  animation: shine 1.6s ease-out 0.2s 1;
}

@keyframes shine {
  to { left: 130%; }
}

.achievement-icon {
  filter: drop-shadow(0 0 8px rgba(45, 212, 191, 0.55));
  animation: pop-in 0.6s cubic-bezier(0.18, 1.4, 0.4, 1) 1;
}

@keyframes pop-in {
  0% { transform: scale(0.2) rotate(-25deg); }
  60% { transform: scale(1.25) rotate(8deg); }
  100% { transform: scale(1) rotate(0); }
}

.sparkle {
  display: inline-block;
  animation: twinkle 1.4s ease-in-out infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 0.4; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1.15); }
}

.toast-enter-active {
  transition: all 0.5s cubic-bezier(0.18, 1.2, 0.4, 1);
}

.toast-leave-active {
  transition: all 0.4s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-28px) scale(0.9);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
